import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ArrowLeft, Download, Edit, Trash2, Loader2, Shield, CheckCircle, Lock } from 'lucide-react';
import { pvService, clubInfoService, handleApiError } from '../services/api';
import { useToast } from '../hooks/use-toast';

const PVPreview = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const [pv, setPV] = useState(null);
  const [clubInfo, setClubInfo] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [validating, setValidating] = useState(false);

  // Load PV data
  const loadPV = async () => {
    try {
      setLoading(true);
      
      // Check for temporary PV first (preview from form)
      if (id && id.startsWith('temp-')) {
        const tempPV = localStorage.getItem('tempPV');
        if (tempPV) {
          setPV(JSON.parse(tempPV));
          setLoading(false);
          return;
        }
      }

      // Load from API
      const [pvData, clubInfoData] = await Promise.all([
        pvService.getById(id),
        clubInfoService.get().catch(() => null) // Don't fail if club info is not available
      ]);
      setPV(pvData);
      setClubInfo(clubInfoData);
    } catch (error) {
      const errorMessage = handleApiError(error);
      toast({
        title: "Erreur",
        description: `Impossible de charger le PV: ${errorMessage}`,
        variant: "destructive"
      });
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPV();
    
    // Cleanup temporary data when leaving
    return () => {
      if (id && id.startsWith('temp-')) {
        localStorage.removeItem('tempPV');
      }
    };
  }, [id]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleExportPDF = async () => {
    // Don't allow export of temporary PVs
    if (id && id.startsWith('temp-')) {
      toast({
        title: "Export impossible",
        description: "Vous devez d'abord sauvegarder le PV pour pouvoir l'exporter.",
        variant: "destructive"
      });
      return;
    }

    try {
      setExporting(true);
      await pvService.exportPdf(id);
      toast({
        title: "PDF exporté",
        description: "Le fichier PDF a été téléchargé avec succès.",
      });
    } catch (error) {
      const errorMessage = handleApiError(error);
      toast({
        title: "Erreur d'export",
        description: `Impossible d'exporter le PDF: ${errorMessage}`,
        variant: "destructive"
      });
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = () => {
    // Don't allow deletion of temporary PVs
    if (id && id.startsWith('temp-')) {
      toast({
        title: "Suppression impossible",
        description: "Vous ne pouvez pas supprimer un PV en cours de création.",
        variant: "destructive"
      });
      return;
    }
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      setDeleting(true);
      await pvService.delete(id);
      
      toast({
        title: "PV supprimé",
        description: "Le procès-verbal a été supprimé avec succès.",
      });
      
      // Notify HomePage to update
      window.dispatchEvent(new CustomEvent('pvUpdated'));
      
      navigate('/dashboard');
    } catch (error) {
      const errorMessage = handleApiError(error);
      toast({
        title: "Erreur",
        description: `Impossible de supprimer le PV: ${errorMessage}`,
        variant: "destructive"
      });
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // Handle PV validation
  const handleValidation = async (newStatus) => {
    // Don't allow validation of temporary PVs
    if (id && id.startsWith('temp-')) {
      toast({
        title: "Validation impossible",
        description: "Vous ne pouvez pas valider un PV en cours de création.",
        variant: "destructive"
      });
      return;
    }

    const statusLabels = {
      validated: "validé",
      locked: "verrouillé",
      draft: "brouillon"
    };

    try {
      setValidating(true);
      const updatedPv = await pvService.validatePv(id, newStatus);
      setPV(updatedPv);
      
      toast({
        title: "Statut modifié",
        description: `PV ${statusLabels[newStatus]} avec succès`,
      });
      
    } catch (error) {
      const errorMessage = handleApiError(error);
      toast({
        title: "Erreur de validation",
        description: `Impossible de modifier le statut: ${errorMessage}`,
        variant: "destructive"
      });
    } finally {
      setValidating(false);
    }
  };

  // Get status badge configuration
  const getStatusBadge = (status) => {
    switch (status) {
      case 'validated':
        return { 
          label: 'Validé', 
          className: 'bg-blue-100 text-blue-700 border border-blue-300',
          icon: <CheckCircle className="w-3 h-3 mr-1" />
        };
      case 'locked':
        return { 
          label: 'Verrouillé', 
          className: 'bg-red-100 text-red-700 border border-red-300',
          icon: <Lock className="w-3 h-3 mr-1" />
        };
      default:
        return { 
          label: 'Brouillon', 
          className: 'bg-gray-100 text-gray-700 border border-gray-300',
          icon: <Edit className="w-3 h-3 mr-1" />
        };
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };

    if (loading) {
      return (
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-green-600" />
              <p className="text-gray-600">Chargement du PV...</p>
            </div>
          </div>
        </div>
      );
    }

    if (!pv) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="text-center">
          <p className="text-gray-500">PV non trouvé</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4">
            Retour à l'accueil
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Header */}
      <div className="flex flex-col gap-4 mb-6">
        {/* Navigation and title row */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              onClick={() => navigate('/dashboard')}
              className="border-indigo-200 text-indigo-600 hover:bg-indigo-50"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <h1 className="text-xl md:text-2xl font-bold text-gray-800">
              Prévisualisation du PV
            </h1>
            {pv && !id?.startsWith('temp-') && (
              <Badge className={getStatusBadge(pv.status).className}>
                {getStatusBadge(pv.status).icon}
                {getStatusBadge(pv.status).label}
              </Badge>
            )}
          </div>
          
          {/* Primary action button */}
          <Button
            onClick={handleExportPDF}
            disabled={loading || deleting || exporting || id?.startsWith('temp-')}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {exporting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Export...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Extraire PDF
              </>
            )}
          </Button>
        </div>
        
        {/* Action buttons row - only if not temporary PV */}
        {!id?.startsWith('temp-') && pv && (
          <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
            {/* Edit button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/pv/${id}/edit`)}
              disabled={loading || deleting || exporting || pv?.status === 'locked'}
              className="border-blue-200 text-blue-600 hover:bg-blue-50"
              title={pv?.status === 'locked' ? 'PV verrouillé - modification impossible' : ''}
            >
              <Edit className="w-4 h-4 mr-2" />
              Modifier
            </Button>
            
            {/* Status-dependent buttons */}
            {pv.status === 'draft' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleValidation('validated')}
                disabled={loading || deleting || exporting || validating}
                className="border-blue-200 text-blue-600 hover:bg-blue-50"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Valider
              </Button>
            )}
            
            {(pv.status === 'draft' || pv.status === 'validated') && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleValidation('locked')}
                disabled={loading || deleting || exporting || validating}
                className="border-orange-200 text-orange-600 hover:bg-orange-50"
              >
                <Lock className="w-4 h-4 mr-2" />
                Verrouiller
              </Button>
            )}
            
            {pv.status === 'validated' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleValidation('draft')}
                disabled={loading || deleting || exporting || validating}
                className="border-gray-200 text-gray-600 hover:bg-gray-50"
              >
                <Edit className="w-4 h-4 mr-2" />
                Remettre en brouillon
              </Button>
            )}
            
            {/* Delete button - only if not locked */}
            {pv?.status !== 'locked' && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDelete}
                disabled={loading || deleting || exporting}
                className="border-red-200 text-red-600 hover:bg-red-50"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Suppression...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer
                  </>
                )}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Document PV */}
      <Card className="bg-white shadow-lg border-gray-200">
        <CardContent className="p-8">
          {/* En-tête du document */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-4">
              {clubInfo?.display_name || clubInfo?.name || 'Club de Tennis'}
            </h1>
            <h2 className="text-xl font-semibold text-gray-700 mb-6">
              Procès-verbal du comité
            </h2>
            
            {/* Informations générales */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left max-w-2xl mx-auto mb-8">
              <div className="flex items-center gap-2">
                <span className="text-blue-600">📅</span>
                <span className="font-medium">Date :</span>
                <span>{formatDate(pv.date)}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-red-600">🕐</span>
                <span className="font-medium">Heure :</span>
                <span>{pv.time}</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-green-600">👥</span>
                <span>
                  <span className="font-medium">Présents :</span> {pv.presents.join(', ') || 'Aucun'}
                </span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-orange-600">🚫</span>
                <span>
                  <span className="font-medium">Excusés :</span> {pv.excuses.join(', ') || 'Aucun'}
                </span>
              </div>
            </div>
          </div>

          <hr className="border-gray-300 mb-8" />

          {/* Contenu du PV */}
          <div className="space-y-8">
            {/* 1. Approbation du dernier PV */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <span className="text-green-600">✅</span>
                1. Approbation du dernier PV
              </h3>
              <p className="text-gray-700 ml-6 leading-relaxed">
                {(pv.approbationDernierPV || pv.approbation_dernier_pv) || 'Aucune information renseignée.'}
              </p>
            </div>

            {/* 2. Suivi des décisions précédentes */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <span className="text-blue-600">📋</span>
                2. Suivi des décisions précédentes
              </h3>
              <p className="text-gray-700 ml-6 leading-relaxed">
                {(pv.suiviDecisions || pv.suivi_decisions) || 'Aucune information renseignée.'}
              </p>
            </div>

            {/* 3. Points à l'ordre du jour */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <span className="text-purple-600">📝</span>
                3. Points à l'ordre du jour
              </h3>
              <div className="ml-6">
                {/* Support both camelCase (new) and snake_case (old) field names */}
                {((pv.pointsOrdreJour || pv.points_ordre_jour) && (pv.pointsOrdreJour || pv.points_ordre_jour).length > 0) ? (
                  <ol className="space-y-3">
                    {(pv.pointsOrdreJour || pv.points_ordre_jour).map((point, index) => {
                      // Handle both old format (string) and new format (object)
                      if (typeof point === 'string' && point.trim()) {
                        // Old format: string
                        return (
                          <li key={index} className="text-gray-700 leading-relaxed">
                            {index + 1}. {point}
                          </li>
                        );
                      } else if (typeof point === 'object' && point.titre && point.titre.trim()) {
                        const notes = point.notes || [];
                        return (
                          <li key={index} className="text-gray-700 leading-relaxed">
                            <div className="flex items-start gap-2">
                              <span>{index + 1}. {point.titre}</span>
                              {point.traite && (
                                <span className="text-green-600 text-base font-bold ml-1">&#10003;</span>
                              )}
                            </div>
                            {notes.length > 0 && (
                              <div className="mt-2 ml-4 space-y-2">
                                {notes.map((note, noteIdx) => (
                                  <div key={noteIdx} className={`border-l-2 pl-3 ${note.termine ? 'border-green-300' : 'border-blue-200'}`}>
                                    <span className={`text-xs font-semibold ${note.termine ? 'text-green-600' : 'text-blue-600'}`}>
                                      {note.date}
                                      {note.termine && <span className="ml-1 bg-green-100 text-green-700 px-1 py-0.5 rounded text-[10px]">Terminé</span>}
                                    </span>
                                    <p className={`text-sm whitespace-pre-wrap ${note.termine ? 'text-gray-400 line-through' : 'text-gray-600'}`}>{note.texte}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                            {(!notes || notes.length === 0) && point.remarques && point.remarques.trim() && (
                              <div className="mt-2 ml-4 space-y-2">
                                {point.remarques.split('\n').reduce((acc, line) => {
                                  const dateMatch = line.match(/^\[(.+?)\]\s*[-:]?\s*(.*)/);
                                  if (dateMatch) {
                                    acc.push({ date: dateMatch[1], texte: dateMatch[2] || '' });
                                  } else if (acc.length > 0) {
                                    acc[acc.length - 1].texte += '\n' + line;
                                  } else {
                                    acc.push({ date: '', texte: line });
                                  }
                                  return acc;
                                }, []).map((note, nIdx) => (
                                  <div key={nIdx} className="border-l-2 border-blue-200 pl-3">
                                    {note.date && <span className="text-xs font-semibold text-blue-600 block">{note.date}</span>}
                                    <p className="text-gray-600 text-sm whitespace-pre-wrap">{note.texte}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                          </li>
                        );
                      }
                      return null; // Skip invalid points
                    }).filter(Boolean)}
                  </ol>
                ) : (
                  <p className="text-gray-700">Aucun point renseigné.</p>
                )}
              </div>
            </div>

            {/* 4. Divers / Propositions individuelles */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                4. Divers / Propositions individuelles
              </h3>
              <div className="ml-6 space-y-3">
                {/* Propositions individuelles structurées */}
                {(() => {
                  const propositionsIndividuelles = pv.propositionsIndividuelles || pv.propositions_individuelles;
                  const diversPropositions = pv.diversPropositions || pv.divers_propositions;
                  
                  return (
                    <>
                      {propositionsIndividuelles && propositionsIndividuelles.length > 0 && (
                        <div className="space-y-2">
                          {propositionsIndividuelles.map((proposition, index) => (
                            <div key={index} className={`p-3 rounded-lg ${
                              proposition.statut === 'validé' ? 'bg-green-50 border border-green-200' :
                              proposition.statut === 'refusé' ? 'bg-red-50 border border-red-200' :
                              'bg-gray-50'
                            }`}>
                              <div className="flex items-start gap-2">
                                {/* Indicateur de statut */}
                                <div className="flex-shrink-0 mt-0.5">
                                  {proposition.statut === 'validé' && (
                                    <span className="text-green-600 font-bold text-lg" title="Proposition validée">✓</span>
                                  )}
                                  {proposition.statut === 'refusé' && (
                                    <span className="text-red-600 font-bold text-lg" title="Proposition refusée">✗</span>
                                  )}
                                  {!proposition.statut && (
                                    <span className="text-gray-400 font-bold text-lg" title="En discussion">•</span>
                                  )}
                                </div>
                                <div className="flex-1">
                                  <span className="font-semibold text-gray-700">{proposition.membre}</span>
                                  <span className="text-gray-600"> propose : </span>
                                  <span className="text-gray-700">{proposition.contenu}</span>
                                  
                                  {/* Remarques sur la décision */}
                                  {proposition.remarques && proposition.statut === 'refusé' && (
                                    <div className="mt-2 p-2 bg-red-100 rounded text-sm">
                                      <span className="font-medium text-red-800">Remarques :</span>
                                      <span className="text-red-700 ml-1">{proposition.remarques}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Points divers généraux */}
                      {diversPropositions && (
                        <div className="mt-4">
                          {propositionsIndividuelles && propositionsIndividuelles.length > 0 && (
                            <h4 className="font-medium text-gray-700 mb-2">Autres points divers :</h4>
                          )}
                          <div className="p-3 bg-gray-50 rounded-lg">
                            <div className="whitespace-pre-wrap text-gray-700">{diversPropositions}</div>
                          </div>
                        </div>
                      )}
                      
                      {/* Si aucun contenu */}
                      {(!propositionsIndividuelles || propositionsIndividuelles.length === 0) && !diversPropositions && (
                        <p className="text-gray-700">Aucune proposition renseignée.</p>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>

            {/* 5. Prochaines étapes et tâches attribuées */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <span className="text-indigo-600">📋</span>
                5. Prochaines étapes et tâches attribuées
              </h3>
              <div className="ml-6">
                {/* Support both camelCase (new) and snake_case (old) field names */}
                {(() => {
                  const taches = pv.prochainEtapes || pv.prochaines_etapes;
                  const taskSort = pv.taskSort || pv.task_sort || 'statut';
                  const taskSortDirection = pv.taskSortDirection || pv.task_sort_direction || 'asc';
                  return taches && taches.filter(etape => etape.tache && etape.tache.trim()).length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-300">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="border border-gray-300 px-4 py-2 text-left font-medium w-2/5">
                            Tâche
                            {taskSort === 'tache' && (
                              <span className="ml-1 text-green-600">
                                {taskSortDirection === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </th>
                          <th className="border border-gray-300 px-4 py-2 text-left font-medium w-1/5">
                            Responsable
                            {taskSort === 'responsable' && (
                              <span className="ml-1 text-green-600">
                                {taskSortDirection === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </th>
                          <th className="border border-gray-300 px-4 py-2 text-left font-medium w-1/5">
                            Délai
                            {taskSort === 'delai' && (
                              <span className="ml-1 text-green-600">
                                {taskSortDirection === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </th>
                          <th className="border border-gray-300 px-4 py-2 text-left font-medium w-1/5">
                            Statut
                            {taskSort === 'statut' && (
                              <span className="ml-1 text-green-600">
                                {taskSortDirection === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          // Appliquer le même tri que dans PVForm
                          let sortedTasks = [...taches.filter(etape => etape.tache && etape.tache.trim())];
                          
                          sortedTasks.sort((a, b) => {
                            let comparison = 0;
                            
                            switch (taskSort) {
                              case 'statut':
                                const statusOrder = { 'En cours': 1, 'En suspens': 2, 'Terminé': 3 };
                                comparison = (statusOrder[a.statut] || 0) - (statusOrder[b.statut] || 0);
                                break;
                              case 'responsable':
                                comparison = (a.responsable || '').localeCompare(b.responsable || '');
                                break;
                              case 'delai':
                                if (!a.delai && !b.delai) comparison = 0;
                                else if (!a.delai) comparison = 1;
                                else if (!b.delai) comparison = -1;
                                else comparison = new Date(a.delai) - new Date(b.delai);
                                break;
                              case 'tache':
                                comparison = (a.tache || '').localeCompare(b.tache || '');
                                break;
                              default:
                                comparison = 0;
                            }
                            
                            return taskSortDirection === 'desc' ? -comparison : comparison;
                          });
                          
                          return sortedTasks.map((etape, index) => (
                            <tr key={index}>
                              <td className="border border-gray-300 px-4 py-2 break-words text-gray-700">{etape.tache}</td>
                              <td className="border border-gray-300 px-4 py-2 whitespace-nowrap text-gray-700">{etape.responsable || 'Non assigné'}</td>
                              <td className="border border-gray-300 px-4 py-2 whitespace-nowrap text-gray-700">
                                {etape.delai ? formatDate(etape.delai) : 'Non défini'}
                              </td>
                              <td className="border border-gray-300 px-4 py-2 whitespace-nowrap">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  etape.statut === 'Terminé' 
                                    ? 'bg-green-100 text-green-700' 
                                    : etape.statut === 'En suspens'
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {etape.statut || 'En cours'}
                                </span>
                              </td>
                            </tr>
                          ));
                        })()}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-700">Aucune tâche renseignée.</p>
                );
                })()}
              </div>
            </div>

            {/* 6. Date du prochain comité */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <span className="text-teal-600">📅</span>
                6. Date du prochain comité
              </h3>
              <p className="text-gray-700 ml-6 leading-relaxed">
                {(pv.prochainComite || pv.prochain_comite) ? formatDate(pv.prochainComite || pv.prochain_comite) : 'Date non définie.'}
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-12 pt-8 border-t border-gray-300 text-center text-sm text-gray-500">
            <p>
              Procès-verbal généré le {new Date().toLocaleDateString('fr-FR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Modal de confirmation de suppression */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="bg-white max-w-md mx-4">
            <CardContent className="p-6">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Supprimer le PV
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  Êtes-vous sûr de vouloir supprimer le procès-verbal du {formatDate(pv.date)} ? 
                  Cette action est irréversible.
                </p>
                <div className="flex gap-3 justify-center">
                  <Button
                    variant="outline"
                    onClick={cancelDelete}
                    disabled={deleting}
                    className="border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Annuler
                  </Button>
                  <Button
                    onClick={confirmDelete}
                    disabled={deleting}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    {deleting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Suppression...
                      </>
                    ) : (
                      'Supprimer'
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
    </div>
  );
};

export default PVPreview;